import { Server, Clock, Hash, Globe } from 'lucide-react'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PepTable } from './pep-table'
import type { PdpInstance } from '@/types/infrastructure'

/**
 * Format a date string as relative time (e.g. "12 seconds ago")
 */
function formatRelativeTime(dateString: string): string {
  const now = Date.now()
  const then = new Date(dateString).getTime()
  const diffMs = now - then

  if (diffMs < 0) return 'just now'

  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `${seconds}s ago`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

interface PdpStatusCardProps {
  readonly pdp: PdpInstance
}

/**
 * Card displaying a single PDP instance with its PEP instances
 */
export function PdpStatusCard({ pdp }: PdpStatusCardProps) {
  const isConnected = pdp.status === 'connected'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Server className="size-4 text-muted-foreground" />
          <CardDescription>PDP Instance</CardDescription>
        </div>
        <CardTitle className="text-lg font-semibold">{pdp.pdpId}</CardTitle>
        <CardAction>
          <Badge variant={isConnected ? 'default' : 'destructive'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Hash className="size-3" />
              Version
            </div>
            <div className="font-mono">{pdp.version}</div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Globe className="size-3" />
              Address
            </div>
            <div className="font-mono truncate" title={pdp.address}>{pdp.address}</div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="size-3" />
              Last Heartbeat
            </div>
            <div>{formatRelativeTime(pdp.lastHeartbeat)}</div>
          </div>
          <div className="space-y-1">
            <div className="text-muted-foreground">Policies</div>
            <div className="font-semibold tabular-nums">{pdp.policyCount}</div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          Registered {new Date(pdp.registeredAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>

        <Separator />

        <div>
          <h4 className="mb-2 text-sm font-medium">
            Enforcement Points ({pdp.peps.length})
          </h4>
          <PepTable peps={pdp.peps} />
        </div>
      </CardContent>
    </Card>
  )
}
